import { motion } from "framer-motion";
import { ArrowUpRight, CloudSun, Moon, CalendarDays, Sprout } from "lucide-react";

const FEATURES = [
  {
    icon: CloudSun,
    title: "Reads Your Skies",
    text: "Live local weather, 5-day outlook, humidity, wind, and UV — woven into every piece of counsel.",
  },
  {
    icon: Moon,
    title: "Daily Whisper",
    text: "Press Summon and the Sage weaves today's weather, your plants, and the season into focused guidance.",
  },
  {
    icon: Sprout,
    title: "Knows Your Roster",
    text: "Browse the seeded plant library or add your own. The more the Sage knows, the sharper the advice.",
  },
  {
    icon: CalendarDays,
    title: "Season Planner",
    text: "Organize tasks by week or month, with sage guidance for every stretch of the growing year.",
  },
];

export default function GardenSage() {
  return (
    <main data-testid="garden-sage-page" className="pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p className="overline-tag mb-6">A Sister Project · From the Same Family</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-zinc-100 leading-[1.02]">
              The Garden <span className="italic text-saffron">Sage</span>
            </h1>
            <p className="mt-6 font-display text-2xl sm:text-3xl italic text-zinc-300 leading-snug">
              Tend by the moon, harvest by the sun.
            </p>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400">
              Our gardener's handbook, distilled into an app. The Sage reads your local skies and your
              living roster of plants — then whispers the small, right things to do today. Born from the
              same Mount Albert soil as everything on this table.
            </p>
            <a
              href="https://thegardensage.app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="garden-sage-cta"
              className="group mt-12 inline-flex items-center gap-3 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors focus-ring"
            >
              Visit thegardensage.app
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="overflow-hidden rounded-t-[10rem] border border-line">
              <img
                src="/images/harvest-flatlay.webp"
                alt="A single morning's harvest laid out on the farm table"
                className="w-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-600">
              Grown here, guided there
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center border border-line bg-wine-deep/20 p-10 lg:p-14"
        >
          <div className="lg:col-span-5 flex justify-center">
            <motion.div
              animate={{ rotate: [0, -1.5, 1.5, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative group"
            >
              <div className="overflow-hidden rounded-t-[10rem] border border-line max-w-sm">
                <img
                  src="/images/sage-scarecrow.webp"
                  alt="The resident Sage — a scarecrow standing watch in the greenhouse among the marigolds"
                  className="w-full object-cover"
                />
              </div>
              <div
                data-testid="sage-whisper-bubble"
                className="absolute -top-4 -right-6 rotate-6 rounded-2xl border border-saffron/40 bg-obsidian px-5 py-3 text-xs italic text-saffron shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              >
                *whispers* …plant the garlic in fall
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline-tag mb-6">Meet the Resident Sage</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-zinc-100 leading-tight">
              He doesn't say much. <span className="italic text-saffron">But he's never wrong.</span>
            </h2>
            <p className="mt-8 text-base leading-relaxed text-zinc-400 max-w-xl">
              Every farm has one quiet know-it-all. Ours stands among the marigolds in the greenhouse,
              scaring off exactly zero crows and somehow predicting every frost. We bottled his entire
              personality into the app — same patience, same timing, considerably better wardrobe
              options in the settings menu.
            </p>
            <p className="mt-6 text-sm text-zinc-500 italic">
              Hover over him. He has opinions.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`sage-feature-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="border border-line bg-surface p-8 flex flex-col gap-4 hover:border-saffron/40 transition-colors duration-500"
            >
              <f.icon className="w-7 h-7 text-saffron" />
              <h3 className="font-display text-xl text-zinc-100">{f.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 border border-line bg-forest/30 p-10 flex flex-wrap items-center justify-between gap-6"
        >
          <p className="font-display text-2xl sm:text-3xl text-zinc-100 max-w-xl leading-snug">
            Free to wander. <span className="italic text-saffron">Your hardiness zone, your plants, your plan.</span>
          </p>
          <a
            href="https://thegardensage.app"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="garden-sage-footer-cta"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:text-saffron-hover transition-colors focus-ring"
          >
            Open the Sage <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </main>
  );
}
